import { useEffect, useMemo, useState } from "react";
import { useAppSelector } from "../../redux/store/hooks";
import {
  useGetOrganizationsQuery,
  useGetEmailTemplatesQuery,
  useUpdateEmailTemplateContentMutation,
  useUpdateEmailTemplateRecipientsMutation,
  ORG_NOTIFY_ROLES,
  EMAIL_TEMPLATE_KEYS,
} from "../../redux/api";
import type { EmailTemplate } from "../../redux/api";
import { RichTextEditor } from "../../components/RichTextEditor";
import { Field } from "../../components/Field";

const TEMPLATE_LABELS: Record<string, string> = {
  "password-sent": "Password sent to new user",
  "user-created": "New team member added",
  "product-created": "Product created",
  "product-updated": "Product updated",
  "product-deleted": "Product deleted",
};

type ContentDraft = { subject: string; body: string; enabled: boolean };

// Settings → Email Templates.
//  - Platform owner: picks any organization, edits subject/body/enabled AND
//    recipient roles for each of the 5 event templates.
//  - Organization owner: sees their own org's templates read-only, but can
//    still edit which roles get notified (everything except "password sent",
//    which always goes to the affected user, not a role).
const EmailTemplatesScreen = () => {
  const role = useAppSelector((s) => s.layout.user.role);
  const ownOrgId = useAppSelector((s) => s.layout.user.organizationId);
  const isPlatformOwner = role === "platform owner";
  const isOrgOwner = role === "owner";

  const { data: organizations = [] } = useGetOrganizationsQuery(undefined, {
    skip: !isPlatformOwner,
  });
  const [orgId, setOrgId] = useState<string>("");

  useEffect(() => {
    if (isPlatformOwner && !orgId && organizations.length) {
      setOrgId(organizations[0].id);
    }
  }, [isPlatformOwner, organizations, orgId]);

  const effectiveOrgId = isPlatformOwner ? orgId : ownOrgId ?? "";

  const {
    data: templates = [],
    isLoading,
    error,
    refetch,
  } = useGetEmailTemplatesQuery(effectiveOrgId, { skip: !effectiveOrgId });

  const [updateContent, { isLoading: savingContent }] =
    useUpdateEmailTemplateContentMutation();
  const [updateRecipients, { isLoading: savingRecipients }] =
    useUpdateEmailTemplateRecipientsMutation();

  const [drafts, setDrafts] = useState<Record<string, ContentDraft>>({});
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  // Per-field validation messages — shown directly under the offending input
  // (with a red border) instead of the single top-of-form banner.
  const [fieldErrors, setFieldErrors] = useState<Record<string, string | undefined>>({});
  // Which one of the 5 templates is shown — defaults to the first.
  const [selectedTemplateKey, setSelectedTemplateKey] = useState<string>(
    EMAIL_TEMPLATE_KEYS[0],
  );

  useEffect(() => {
    setDrafts(
      Object.fromEntries(
        templates.map((t) => [
          t.templateKey,
          { subject: t.subject, body: t.body, enabled: t.enabled },
        ]),
      ),
    );
  }, [templates]);

  const dirty = useMemo(() => {
    if (!editingKey) return false;
    const t = templates.find((x) => x.templateKey === editingKey);
    const d = drafts[editingKey];
    if (!t || !d) return false;
    return (
      t.subject !== d.subject || t.body !== d.body || t.enabled !== d.enabled
    );
  }, [editingKey, drafts, templates]);

  const setDraft = (key: string, patch: Partial<ContentDraft>) => {
    setDrafts((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));
    const clearedKeys = Object.keys(patch) as (keyof ContentDraft)[];
    setFieldErrors((prev) => {
      let next = prev;
      for (const k of clearedKeys) {
        if (next[k]) next = { ...next, [k]: undefined };
      }
      return next;
    });
  };

  const handleSaveContent = async (templateKey: string) => {
    setFormError(null);
    const draft = drafts[templateKey];

    const errors: Record<string, string> = {};
    if (!draft.subject.trim()) errors.subject = "Subject is required.";
    if (!draft.body.trim()) errors.body = "Body is required.";

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});

    try {
      await updateContent({
        organizationId: effectiveOrgId,
        templateKey,
        input: draft,
      }).unwrap();
      setEditingKey(null);
    } catch (err) {
      setFormError(
        (err as { message?: string })?.message ?? "Failed to save template.",
      );
    }
  };

  const toggleRole = async (template: EmailTemplate, role: string) => {
    const notifyRoles = template.notifyRoles.includes(role)
      ? template.notifyRoles.filter((r) => r !== role)
      : [...template.notifyRoles, role];
    try {
      await updateRecipients({
        organizationId: effectiveOrgId,
        templateKey: template.templateKey,
        input: { notifyRoles },
      }).unwrap();
    } catch (err) {
      window.alert(
        (err as { message?: string })?.message ??
          "Failed to update recipients.",
      );
    }
  };

  const errorMessage = error
    ? (error as { message?: string }).message ?? "Failed to load templates"
    : null;

  const selectedTemplate = templates.find(
    (t) => t.templateKey === selectedTemplateKey,
  );

  if (!isPlatformOwner && !isOrgOwner) {
    return (
      <div className="dashboard">
        <section className="panel">
          <div className="profile-empty">
            Only the platform owner and organization owners can view email
            templates.
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <section className="panel">
        <div className="panel__head">
          <div>
            <h2>Email Templates</h2>
            <h3 className="panel__section-title" style={{ marginTop: "0.5rem", marginBottom: 0 }}>
              {isPlatformOwner ? "Select organization" : "Your organization"}
            <p className="panel__sub">{templates.length} template(s)</p>
            </h3>
          </div>
          <div className="toolbar">
            <select
              className="select"
              value={selectedTemplateKey}
              onChange={(e) => setSelectedTemplateKey(e.target.value)}
            >
              {EMAIL_TEMPLATE_KEYS.map((key) => (
                <option key={key} value={key}>
                  {TEMPLATE_LABELS[key] ?? key}
                </option>
              ))}
            </select>
            {isPlatformOwner && (
              <select
                className="select"
                value={orgId}
                onChange={(e) => setOrgId(e.target.value)}
              >
                {organizations.length === 0 ? (
                  <option value="">No organizations</option>
                ) : (
                  organizations.map((org) => (
                    <option key={org.id} value={org.id}>
                      {org.name}
                    </option>
                  ))
                )}
              </select>
            )}
            <button type="button" className="btn" onClick={() => refetch()}>
              Refresh
            </button>
          </div>
        </div>

        <div className="panel__body">
          {!effectiveOrgId ? (
            <div className="profile-empty">
              {isPlatformOwner
                ? "Select an organization to view its templates."
                : "No organization found for your account."}
            </div>
          ) : isLoading ? (
            <p>Loading…</p>
          ) : errorMessage ? (
            <div className="table-error">
              <span>{errorMessage}</span>
              <button type="button" className="btn btn--ghost" onClick={() => refetch()}>
                Try again
              </button>
            </div>
          ) : !selectedTemplate ? (
            <div className="profile-empty">Template not found.</div>
          ) : (
            (() => {
              const t = selectedTemplate;
              const draft = drafts[t.templateKey] ?? {
                subject: t.subject,
                body: t.body,
                enabled: t.enabled,
              };
              const isEditingThis = editingKey === t.templateKey;
              const isRoleBased = t.templateKey !== "password-sent";

              return (
                <section className="panel">
                  <div className="panel__head">
                    <div>
                      <h2>{TEMPLATE_LABELS[t.templateKey] ?? t.templateKey}</h2>
                      <p className="panel__sub">
                        {t.enabled ? "Enabled" : "Disabled"}
                      </p>
                    </div>
                    {isPlatformOwner && !isEditingThis && (
                      <button
                        type="button"
                        className="btn"
                        onClick={() => {
                          setEditingKey(t.templateKey);
                          setFormError(null);
                          setFieldErrors({});
                        }}
                      >
                        Edit
                      </button>
                    )}
                  </div>

                  <div className="panel__body">
                    {isEditingThis && formError ? (
                      <div className="form-error">{formError}</div>
                    ) : null}

                    <div className="form-grid">
                      <Field label="Subject" error={fieldErrors.subject} full>
                        <input
                          value={draft.subject}
                          disabled={!isEditingThis}
                          onChange={(e) =>
                            setDraft(t.templateKey, { subject: e.target.value })
                          }
                        />
                        {!isPlatformOwner && (
                          <span className="field__readonly">View only</span>
                        )}
                      </Field>
                      <Field label="Body" error={fieldErrors.body} full>
                        <RichTextEditor
                          value={draft.body}
                          onChange={(html) =>
                            setDraft(t.templateKey, { body: html })
                          }
                          readOnly={!isEditingThis}
                          placeholder="Write the email…"
                        />
                        {!isPlatformOwner && (
                          <span className="field__readonly">View only</span>
                        )}
                      </Field>
                      <label className="field field--checkbox">
                        <input
                          type="checkbox"
                          checked={draft.enabled}
                          disabled={!isEditingThis}
                          onChange={(e) =>
                            setDraft(t.templateKey, { enabled: e.target.checked })
                          }
                        />
                        Enabled
                      </label>
                    </div>

                    {isPlatformOwner && (
                      <div className="form-actions">
                        {isEditingThis ? (
                          <>
                            <button
                              type="button"
                              className="btn"
                              onClick={() => {
                                setEditingKey(null);
                                setDraft(t.templateKey, {
                                  subject: t.subject,
                                  body: t.body,
                                  enabled: t.enabled,
                                });
                                setFieldErrors({});
                              }}
                              disabled={savingContent}
                            >
                              Cancel
                            </button>
                            <button
                              type="button"
                              className="btn btn--primary"
                              onClick={() => handleSaveContent(t.templateKey)}
                              disabled={savingContent || !dirty}
                            >
                              {savingContent ? "Saving…" : "Save changes"}
                            </button>
                          </>
                        ) : null}
                      </div>
                    )}

                    {isRoleBased ? (
                      <div className="field field--full" style={{ marginTop: "1rem" }}>
                        <span>Notify roles</span>
                        <div className="toolbar" style={{ flexWrap: "wrap" }}>
                          {ORG_NOTIFY_ROLES.map((r) => (
                            <label
                              key={r}
                              className="pill pill--neutral"
                              style={{ cursor: "pointer", textTransform: "capitalize" }}
                            >
                              <input
                                type="checkbox"
                                checked={t.notifyRoles.includes(r)}
                                onChange={() => toggleRole(t, r)}
                                disabled={savingRecipients}
                                style={{ marginRight: "0.4rem" }}
                              />
                              {r}
                            </label>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <p className="panel__sub" style={{ marginTop: "1rem" }}>
                        Always sent to the new user's own email address.
                      </p>
                    )}
                  </div>
                </section>
              );
            })()
          )}
        </div>
      </section>
    </div>
  );
};

export default EmailTemplatesScreen;
